import { useEffect, useMemo, useRef, useState } from 'react';
import * as service from '../../services/bauordnungService';
import { holdIdleExit } from '../../services/idleHold';
import { freigabeWiederholung, halteFreigabeFest, type FreigabeFesthalten, type FreigabeJournal } from '../../services/unterlagenAblage';
import { baueBauordnungFreigabeAuftrag, legeFreigabeAb, type FreigabeAblageArgs } from './ablage/bauordnungAblage';
import {
    KEINE_LAEUFE, ablageVermerken, freigabeSchluessel, journalVermerken, laufAnsicht, laufBeenden, laufStarten,
    laufWeiter, type FreigabeLaeufe, type FreigabeLaufAnsicht, type FreigabeSchluessel, type LaufPhase,
} from './freigabeLauf';
import type { Bauordnung } from './types';
import type { ReportMetadata } from '../../types';

/**
 * Der Lauf nach einer Freigabe — im MODUL, nicht im Reiter (Begruendung in
 * `freigabeLauf.ts`). Der Reiter ruft `freigeben`, `ablegen`, `festhalten` und
 * liest `ansicht`; der asynchrone Ablauf gehoert dem Hook und laeuft weiter,
 * wenn der Reiter ausgehaengt wird.
 *
 * Der Zustand steht zusaetzlich in einer Ref: die Schritte lesen den JETZIGEN
 * Stand, nicht den ihres Renders, und ein Doppelklick sieht den schon
 * gestarteten Lauf sofort. Ist das Modul ausgehaengt, wird nur noch die Ref
 * fortgeschrieben — kein setState mehr. Der Abgleich des Aenderungsdiensts
 * wird nur fuer das Projekt angestossen, das gerade offen ist.
 */

type Schreiben = <T>(vorgang: () => Promise<T>) => Promise<T | null>;

/** Was der Reiter zur Freigabe mitgibt: sein Buendel, die Fassungsbezeichnung und alles fuer Journal und Ablage. */
export interface FreigabeAnstoss extends Omit<FreigabeAblageArgs, '__pdf'> {
    fassung: string;
    /** Stammdaten fuer den Planungsstand (`undefined`: der Helfer liest selbst). */
    metadata?: Partial<ReportMetadata> | null;
}

const LEERER_HINWEIS = 'Der Planungsstand wurde nicht festgehalten.';

/**
 * Nach dem Journal-Schritt (Plan Paketposition B5): die Kennungen der
 * Ausgaben, die er geschrieben oder vorgefunden hat — der Abschlussschritt
 * fragt mit ihnen nach, welchen Bestandteil die Freigabe erledigt hat, und
 * bietet nach einer FREIGABE die Leistungsbuchung an. `anlass` sagt, ob es
 * die Freigabe selbst war oder „Erneut festhalten".
 */
export type NachJournal = (
    projectId: string,
    /** Eigene Ausgaben (Ausloeser) und schon vorher liegende (nur Anzeige) — `FreigabeJournal`. */
    ausgaben: { ausgabeIds?: string[]; vorhandeneAusgabeIds?: string[] },
    anlass: 'freigabe' | 'festhalten',
) => void;

export const useFreigabeLauf = (
    projectId: string,
    ordnung: Bauordnung | null,
    schreiben: Schreiben,
    onAbgleichNoetig: () => void,
    onNachJournal?: NachJournal,
) => {
    const [laeufe, setLaeufe] = useState<FreigabeLaeufe>(KEINE_LAEUFE);
    const stand = useRef<FreigabeLaeufe>(KEINE_LAEUFE);
    const eingehaengt = useRef(true);
    const offenesProjekt = useRef(projectId);

    useEffect(() => {
        eingehaengt.current = true;
        return () => { eingehaengt.current = false; };
    }, []);
    useEffect(() => { offenesProjekt.current = projectId; }, [projectId]);

    const anwenden = (neu: FreigabeLaeufe) => {
        stand.current = neu;
        if (eingehaengt.current) setLaeufe(neu);
    };

    /** Startet einen Lauf; `false`, wenn im Projekt schon einer laeuft. */
    const starten = (pid: string, phase: LaufPhase): boolean => {
        const neu = laufStarten(stand.current, pid, phase);
        if (!neu) return false;
        anwenden(neu);
        return true;
    };

    /** Schreibt das Freigabe-Journal — erster Versuch und „Erneut festhalten" gleich. Wirft nie. */
    const journalSchritt = async (pid: string, fuer: FreigabeSchluessel, auftrag: FreigabeFesthalten): Promise<FreigabeJournal | null> => {
        try {
            const journal = await halteFreigabeFest(auftrag);
            anwenden(journalVermerken(stand.current, pid, fuer, journal.status === 'fehler'
                ? { hinweis: journal.hinweis || LEERER_HINWEIS, wiederholung: freigabeWiederholung(auftrag, journal) }
                : null));
            return journal;
        } catch {
            // `halteFreigabeFest` verspricht, nie zu werfen; faellt doch etwas
            // durch, gibt es keine Kennung — der Abschlussschritt sagt das.
            return null;
        } finally {
            // Der Aenderungsdienst soll die frische Fassung vergleichen — aber
            // nur, wenn er noch dieses Projekt zeigt.
            if (eingehaengt.current && offenesProjekt.current === pid) onAbgleichNoetig();
        }
    };

    /**
     * Legt die Dokumente ab. Ueber `schreiben`, damit das Buendel danach neu
     * geladen wird und die Ids am Kopfsatz sichtbar sind. `legeFreigabeAb`
     * wirft nie; ein `null` kaeme nur aus `schreiben` selbst.
     */
    const ablageSchritt = async (pid: string, fuer: FreigabeSchluessel, args: FreigabeAblageArgs) => {
        const loslassen = holdIdleExit('Baustellenordnung wird abgelegt');
        try {
            // Kein Steckbrief-Anker aus der Ablage: der liegt seit der Freigabe
            // im Journal (`baueBauordnungFreigabeAuftrag` → `halteFreigabeFest`).
            const erg = await schreiben(() => legeFreigabeAb(args));
            anwenden(ablageVermerken(stand.current, pid, fuer, erg));
        } finally {
            loslassen();
        }
    };

    const freigeben = async (a: FreigabeAnstoss): Promise<void> => {
        const o = a.bundle.ordnung;
        if (!o || !starten(o.project_id, 'freigabe')) return;
        const pid = o.project_id;
        try {
            const neu = await schreiben(() => service.freigebenBauordnung(a.bundle, a.merkmale, a.fassung, a.ctx));
            const fuer = freigabeSchluessel(neu);
            if (!neu || !fuer) return;
            // Der Planungsstand DIESER Freigabe fuer den Aenderungsdienst — jetzt,
            // aus dem frisch freigegebenen Kopfsatz, nicht erst bei der Ablage:
            // die kann Tage spaeter nachgeholt werden (Stufe 6.1). Scheitert das
            // Journal, gilt die Freigabe trotzdem.
            anwenden(laufWeiter(stand.current, pid, 'journal'));
            const journal = await journalSchritt(pid, fuer, await baueBauordnungFreigabeAuftrag(neu, a.metadata));
            // Abschlussschritt (Plan B5): direkt nach der Freigabe — Buchung
            // anbieten, Bestandteile nachfragen. Unabhaengig davon, ob das
            // Journal gelang: die eigene Position bucht auch ohne Ausgabe.
            onNachJournal?.(pid, { ausgabeIds: journal?.ausgabeIds, vorhandeneAusgabeIds: journal?.vorhandeneAusgabeIds }, 'freigabe');
            // Das PDF entsteht aus dem FREIGEGEBENEN Kopfsatz — aus dem alten
            // Buendel truege es noch den Entwurfsvermerk.
            anwenden(laufWeiter(stand.current, pid, 'ablage'));
            await ablageSchritt(pid, fuer, {
                bundle: { ...a.bundle, ordnung: neu },
                merkmale: a.merkmale,
                projektName: a.projektName,
                projektNummer: a.projektNummer,
                projektAdresse: a.projektAdresse,
                ctx: a.ctx,
            });
        } finally {
            anwenden(laufBeenden(stand.current, pid));
        }
    };

    /** „Erneut ablegen" — nur an einer freigegebenen Fassung. */
    const ablegen = async (args: FreigabeAblageArgs): Promise<void> => {
        const o = args.bundle.ordnung;
        const fuer = freigabeSchluessel(o);
        if (!o || !fuer || !starten(o.project_id, 'ablage')) return;
        try {
            await ablageSchritt(o.project_id, fuer, args);
        } finally {
            anwenden(laufBeenden(stand.current, o.project_id));
        }
    };

    /** „Erneut festhalten" — mit dem Auftrag samt Anker des ersten Versuchs. */
    const festhalten = async (fuer: FreigabeSchluessel, auftrag: FreigabeFesthalten): Promise<void> => {
        const pid = auftrag.projectId;
        if (!starten(pid, 'journal')) return;
        try {
            const journal = await journalSchritt(pid, fuer, auftrag);
            // Erst jetzt liegt die Ausgabe — und erst jetzt hat der Trigger den
            // Bestandteil erledigt (Plan B5).
            if (journal && journal.status !== 'fehler') {
                onNachJournal?.(pid, { ausgabeIds: journal.ausgabeIds, vorhandeneAusgabeIds: journal.vorhandeneAusgabeIds }, 'festhalten');
            }
        } finally {
            anwenden(laufBeenden(stand.current, pid));
        }
    };

    const ansicht: FreigabeLaufAnsicht = useMemo(
        () => laufAnsicht(laeufe, projectId, ordnung),
        [laeufe, projectId, ordnung],
    );

    return { ansicht, freigeben, ablegen, festhalten };
};
